import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ButtonSpinner } from './LoadingSpinner';

interface SegmentedControlProps {
    options: { label: string; value: string }[];
    value: string;
    onChange: (value: string) => void;
    style?: React.CSSProperties;
    className?: string;
    isLoading?: boolean;
    loadingValue?: string;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({
    options,
    value,
    onChange,
    style,
    className,
    isLoading = false,
    loadingValue
}) => {
    const { theme } = useTheme();
    const [buttonWidths, setButtonWidths] = useState<{ [key: string]: number }>({});
    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

    // Measure button widths on mount and when options change
    useEffect(() => {
        const widths: { [key: string]: number } = {};
        options.forEach(option => {
            const button = buttonRefs.current[option.value];
            if (button) {
                widths[option.value] = button.offsetWidth;
            }
        });
        setButtonWidths(widths);
    }, [options]);

    return (
        <div
            className={className}
            style={{
                display: 'flex',
                borderRadius: 8,
                overflow: 'hidden',
                border: `1px solid ${theme.colors.BORDER}`,
                width: 'fit-content',
                position: 'relative',
                ...style,
            }}
        >
            {options.map((option, idx) => {
                const isSelected = value === option.value;
                const isButtonLoading = isLoading && loadingValue === option.value;
                const buttonWidth = buttonWidths[option.value];

                return (
                    <React.Fragment key={option.value}>
                        <button
                            ref={el => buttonRefs.current[option.value] = el}
                            onClick={() => onChange(option.value)}
                            disabled={isLoading}
                            style={{
                                padding: '10px 20px',
                                background: isSelected ? theme.colors.PRIMARY : theme.colors.SURFACE,
                                color: isSelected ? theme.colors.WHITE : theme.colors.TEXT,
                                border: 'none',
                                outline: 'none',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontWeight: 500,
                                transition: 'background 0.2s, color 0.2s',
                                borderLeft: idx !== 0 ? `1px solid ${theme.colors.BORDER}` : undefined,
                                fontSize: '1em',
                                opacity: isLoading && !isButtonLoading ? 0.5 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                width: buttonWidth ? `${buttonWidth}px` : 'auto',
                                minWidth: '120px',
                                visibility: isButtonLoading ? 'hidden' : 'visible',
                                position: 'relative',
                            }}
                        >
                            {option.label}
                        </button>
                        {isButtonLoading && (
                            <button
                                disabled
                                style={{
                                    padding: '10px 20px',
                                    background: isSelected ? theme.colors.PRIMARY : theme.colors.SURFACE,
                                    color: isSelected ? theme.colors.WHITE : theme.colors.TEXT,
                                    border: 'none',
                                    outline: 'none',
                                    cursor: 'not-allowed',
                                    fontWeight: 500,
                                    transition: 'background 0.2s, color 0.2s',
                                    borderLeft: idx !== 0 ? `1px solid ${theme.colors.BORDER}` : undefined,
                                    fontSize: '1em',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    width: buttonWidth ? `${buttonWidth}px` : 'auto',
                                    minWidth: '120px',
                                    position: 'absolute',
                                    left: idx === 0 ? 0 : options.slice(0, idx).reduce((sum, opt) => sum + (buttonWidths[opt.value] || 0), 0),
                                    top: 0,
                                }}
                            >
                                <ButtonSpinner size={20} color={isSelected ? theme.colors.WHITE : theme.colors.PRIMARY} />
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default SegmentedControl; 